import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown } from "lucide-react";
import { RFPsTable } from "../components/organisms/RFPsTable";
import {
  UploadRfpDrawer,
  type UploadKbDocumentFormData,
} from "../components/organisms/UploadRfpDrawer";
import { Button } from "../components/atoms/Button";
import { Breadcrumb } from "../components/molecules/Breadcrumb";
import { useKbQuestions } from "../hooks/useKbQuestions";
import { downloadKbTemplate, uploadKbDocument } from "../services/rfpsService";
import type { KbQuestionSortBy, KbQuestionSortDir, KbQuestionStatus } from "../types/kbQuestion";
import type { KbQuestionItem } from "../types/kbQuestion";

export const RFPsPage = () => {
  const navigate = useNavigate();
  const {
    questions,
    loading,
    error,
    page,
    pageSize,
    totalRows,
    searchInput,
    status,
    sortBy,
    sortDir,
    setSearchInput,
    setStatus,
    setPage,
    setPageSize,
    setSorting,
    reload,
  } = useKbQuestions();
  const [showFilter, setShowFilter] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [templateDownloadError, setTemplateDownloadError] = useState<string | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    [setSearchInput],
  );

  const clearFilters = useCallback(() => {
    setStatus("");
  }, [setStatus]);

  const hasActiveFilters = status !== "";

  const handleUpload = useCallback(
    async ({ file }: UploadKbDocumentFormData) => {
      await uploadKbDocument({ file });
      setIsDrawerOpen(false);
      await reload();
    },
    [reload],
  );

  const handleDownloadTemplate = useCallback(async () => {
    if (isDownloadingTemplate) return;

    setTemplateDownloadError(null);
    setIsDownloadingTemplate(true);

    try {
      const { blob, fileName } = await downloadKbTemplate();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to download template";
      setTemplateDownloadError(message);
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, [isDownloadingTemplate]);

  const handleDownloadAdminGuide = useCallback(() => {
    const fileName = "IntelliDocSuite-RFP-Admin-Guide.pdf";
    const link = document.createElement("a");
    link.href = `/assets/${fileName}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  const handleSortChange = useCallback(
    (nextSortBy: KbQuestionSortBy, nextSortDir: KbQuestionSortDir) => {
      setSorting(nextSortBy, nextSortDir);
    },
    [setSorting],
  );

  const handleActionClick = useCallback(
    (question: KbQuestionItem) => {
      navigate(`/rfps/rfp-question-detail?id=${encodeURIComponent(question.id)}`);
    },
    [navigate],
  );

  return (
    <section className="page-section min-h-0 h-full overflow-hidden">
      <Breadcrumb items={[{ label: "Library", to: "/library" }, { label: "RFPs" }]} />
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-heading">RFPs</h1>
          <p className="mt-1 text-sm text-gray-500">Manage and respond to RFP requests</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleDownloadAdminGuide} className="shrink-0">
            <FileDown className="-ml-0.5 mr-1.5 h-4 w-4" />
            Admin Guide
          </Button>
          <Button
            onClick={handleDownloadTemplate}
            className="shrink-0"
            disabled={isDownloadingTemplate}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-0.5 mr-1.5 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 16.5v2.25A2.25 2.25 0 006.75 21h10.5a2.25 2.25 0 002.25-2.25V16.5M12 3v11.25m0 0l-3.75-3.75M12 14.25l3.75-3.75"
              />
            </svg>
            {isDownloadingTemplate ? "Downloading..." : "Dowload Template"}
          </Button>
          <Button onClick={() => setIsDrawerOpen(true)} className="shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-0.5 mr-1.5 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
              />
            </svg>
            Upload RFP
          </Button>
        </div>
      </div>

      {templateDownloadError && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {templateDownloadError}
        </div>
      )}

      <div className="page-toolbar">
        <div className="relative max-w-sm flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search questions and answers..."
            value={searchInput}
            onChange={handleSearchChange}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFilter((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filter
            {hasActiveFilters && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary-500 text-[10px] font-bold text-white">
                !
              </span>
            )}
          </button>

          {showFilter && (
            <div className="absolute left-0 top-full z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Filters</p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs font-medium text-primary-500 hover:text-primary-600"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="fs" className="mb-1 block text-xs font-medium text-gray-600">
                    Status
                  </label>
                  <select
                    id="fs"
                    value={status}
                    onChange={(e) => setStatus((e.target.value as KbQuestionStatus | "") ?? "")}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
          <button
            type="button"
            onClick={reload}
            className="ml-3 font-medium underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-hidden">
        <RFPsTable
          questions={questions}
          isLoading={loading}
          totalRows={totalRows}
          page={page}
          perPage={pageSize}
          sortBy={sortBy}
          sortDir={sortDir}
          onChangePage={setPage}
          onChangeRowsPerPage={setPageSize}
          onSortChange={handleSortChange}
          onActionClick={handleActionClick}
        />
      </div>

      <UploadRfpDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        mode="kbDocument"
        onUpload={handleUpload}
      />
    </section>
  );
};
